import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { PERMISSIONS, formatMinutes, type AircraftMaintenance } from '@jupi/shared'
import { DISPLAY_STYLE, RemoteContent, useAuth } from '@jupi/ui'
import { useFleet } from '../api'
import { ZoneBadge } from '../components/ZoneBadge'

// Every aircraft at once, as large tiles: one tap to the one being worked on.
// Two across on a tablet in landscape, one in portrait (the default).
export default function FleetPage() {
  const { t } = useTranslation()
  const { data: fleet = [], isLoading, error } = useFleet()
  const { can } = useAuth()

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h4" component="h1">
          {t('huolto.fleetTitle')}
        </Typography>
        <Typography color="text.secondary">{t('huolto.fleetHint')}</Typography>
      </Box>
      {can(PERMISSIONS.inventoryRead, PERMISSIONS.inventoryManage) ? (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardActionArea component={RouterLink} to="/parts" sx={{ minHeight: 88 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                <Icon icon="mdi:package-variant-closed" width={40} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">{t('huolto.partsLink')}</Typography>
                  <Typography color="text.secondary">{t('huolto.partsLinkHint')}</Typography>
                </Box>
                <Icon icon="mdi:chevron-right" width={36} />
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      ) : null}
      <RemoteContent isLoading={isLoading} error={error}>
        {!isLoading && fleet.length === 0 ? <Typography>{t('huolto.noAircraft')}</Typography> : null}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
          {fleet.map((a) => (
            <AircraftTile key={a.registration} aircraft={a} />
          ))}
        </Box>
      </RemoteContent>
    </Stack>
  )
}

function AircraftTile({ aircraft: a }: { aircraft: AircraftMaintenance }) {
  const { t } = useTranslation()
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardActionArea component={RouterLink} to={`/aircraft/${encodeURIComponent(a.registration)}`} sx={{ minHeight: 148 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...DISPLAY_STYLE, fontSize: '2.25rem', lineHeight: 1.1, whiteSpace: 'nowrap' }}>{a.registration}</Typography>
              <Typography color="text.secondary" noWrap>
                {a.aircraftType ?? ''}
              </Typography>
            </Box>
            {a.status ? <ZoneBadge zone={a.status.zone} minutesToDue={a.status.minutesToDue} /> : null}
            <Icon icon="mdi:chevron-right" width={36} />
          </Stack>

          <Typography variant="h6" sx={{ mt: 1.5 }}>
            {a.airframeMinutes != null
              ? `${t('huolto.airframe')} ${formatMinutes(a.airframeMinutes)} h`
              : t('huolto.noAirframe')}
          </Typography>

          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {a.schedule
              ? `${t('huolto.nextMaintenance')}: ${a.schedule.nextType} ${t('huolto.at', { at: formatMinutes(a.schedule.nextDueMinutes) })}`
              : t('huolto.noSchedule')}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}
